import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

// ── PinStatusBadge ───────────────────────────

interface PinStatusStudent {
	pinResetAt?: string | null;
	firstVisitCompleted?: boolean;
}

interface PinStatusBadgeProps {
	student: PinStatusStudent;
	className?: string;
}

export function PinStatusBadge({
	student,
	className = '',
}: PinStatusBadgeProps) {
	let label: string;
	let style: string;
	if (student?.pinResetAt) {
		label = 'Reset Pending';
		style = 'bg-status-warning-bg text-status-warning';
	} else if (student?.firstVisitCompleted) {
		label = 'PIN Set';
		style = 'bg-status-success-bg text-status-success';
	} else {
		label = 'Not Set';
		style = 'bg-surface text-text-muted';
	}
	return (
		<span
			className={`inline-flex items-center px-2.5 py-0.5 rounded-pill text-caption font-medium ${style} ${className}`}>
			{label}
		</span>
	);
}

// ── StatusBadge ──────────────────────────────

interface StatusBadgeProps {
	status: string;
	className?: string;
}

const statusStyles: Record<string, string> = {
	active: 'bg-status-success-bg text-status-success',
	success: 'bg-status-success-bg text-status-success',
	completed: 'bg-status-success-bg text-status-success',
	setup_pending: 'bg-status-warning-bg text-status-warning',
	pending: 'bg-status-warning-bg text-status-warning',
	warning: 'bg-status-warning-bg text-status-warning',
	inactive: 'bg-status-error-bg text-status-error',
	error: 'bg-status-error-bg text-status-error',
	failed: 'bg-status-error-bg text-status-error',
	info: 'bg-status-info-bg text-status-info',
	archived: 'bg-surface text-text-muted',
	neutral: 'bg-surface text-text-muted',
};

export function StatusBadge({ status, className = '' }: StatusBadgeProps) {
	const style = statusStyles[status] || statusStyles.neutral;
	const label = status?.replace(/_/g, ' ');

	return (
		<span
			className={`inline-flex items-center px-2.5 py-0.5 rounded-pill text-caption font-medium capitalize ${style} ${className}`}
			data-testid={`status-badge-${status}`}>
			{label}
		</span>
	);
}

// ── PageHeader ───────────────────────────────

interface PageHeaderProps {
	title: string;
	subtitle?: ReactNode;
	action?: ReactNode;
	testId?: string;
}

export function PageHeader({
	title,
	subtitle,
	action,
	testId = 'page-header',
}: PageHeaderProps) {
	return (
		<div
			className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6'
			data-testid={testId}>
			<div>
				<h1 className='text-heading-1 text-text-primary'>{title}</h1>
				{subtitle && (
					<p className='text-body text-text-secondary mt-1'>{subtitle}</p>
				)}
			</div>
			{action && <div>{action}</div>}
		</div>
	);
}

// ── StatCard ─────────────────────────────────

interface StatCardProps {
	label: string;
	value: string | number;
	icon?: LucideIcon;
	trend?: number;
	color?: string;
	testId?: string;
}

export function StatCard({
	label,
	value,
	icon: Icon,
	trend,
	color = 'bg-brand-orange',
	testId,
}: StatCardProps) {
	return (
		<div
			className='bg-surface-card rounded-card p-5 shadow-card border border-surface-border'
			data-testid={testId}>
			<div className='flex items-start justify-between'>
				<div>
					<p className='text-body-sm text-text-secondary mb-1'>{label}</p>
					<p className='text-heading-1 text-text-primary'>{value}</p>
					{trend !== undefined && (
						<p
							className={`text-caption mt-1 ${trend >= 0 ? 'text-status-success' : 'text-status-error'}`}>
							{trend >= 0 ? '+' : ''}
							{trend}% from last week
						</p>
					)}
				</div>
				{Icon && (
					<div
						className={`w-10 h-10 ${color} rounded-card flex items-center justify-center`}>
						<Icon size={20} className='text-text-inverse' />
					</div>
				)}
			</div>
		</div>
	);
}

// ── EmptyState ───────────────────────────────

interface EmptyStateProps {
	icon?: LucideIcon;
	title: string;
	description: string;
	action?: ReactNode;
	testId?: string;
}

export function EmptyState({
	icon: Icon,
	title,
	description,
	action,
	testId = 'empty-state',
}: EmptyStateProps) {
	return (
		<div
			className='flex flex-col items-center justify-center py-16 text-center'
			data-testid={testId}>
			{Icon && (
				<div className='w-16 h-16 rounded-full bg-surface flex items-center justify-center mb-4'>
					<Icon size={32} className='text-text-muted' />
				</div>
			)}
			<h3 className='text-heading-3 text-text-primary mb-2'>{title}</h3>
			<p className='text-body text-text-secondary max-w-md mb-4'>
				{description}
			</p>
			{action}
		</div>
	);
}

// ── LoadingSpinner ───────────────────────────

interface LoadingSpinnerProps {
	size?: 'sm' | 'md' | 'lg';
	label?: string;
}

export function LoadingSpinner({ size = 'md', label }: LoadingSpinnerProps) {
	const sizes: Record<string, string> = {
		sm: 'w-5 h-5',
		md: 'w-8 h-8',
		lg: 'w-12 h-12',
	};
	return (
		<div
			className='flex flex-col items-center justify-center gap-3 py-12'
			data-testid='loading-spinner'>
			<div
				className={`${sizes[size]} border-3 border-surface-border border-t-brand-orange rounded-full animate-spin`}
			/>
			{label && <p className='text-body-sm text-text-muted'>{label}</p>}
		</div>
	);
}

// ── ButtonSpinner ────────────────────────────

interface ButtonSpinnerProps {
	dark?: boolean;
}

export function ButtonSpinner({ dark = false }: ButtonSpinnerProps) {
	return (
		<div
			className={`w-4 h-4 border-2 rounded-full animate-spin flex-shrink-0 ${
				dark
					? 'border-text-muted/30 border-t-text-muted'
					: 'border-white/30 border-t-white'
			}`}
		/>
	);
}

// ── StatCardSkeleton ─────────────────────────

export function StatCardSkeleton() {
	return (
		<div className='bg-surface-card rounded-card p-5 shadow-card border border-surface-border animate-pulse'>
			<div className='flex items-start justify-between'>
				<div className='flex-1 space-y-2'>
					<div className='h-3 w-24 bg-surface-border rounded' />
					<div className='h-7 w-16 bg-surface-border rounded' />
				</div>
				<div className='w-10 h-10 bg-surface-border rounded-card' />
			</div>
		</div>
	);
}

// ── TableSkeleton ────────────────────────────

interface TableSkeletonProps {
	rows?: number;
	cols?: number;
}

export function TableSkeleton({ rows = 5, cols = 4 }: TableSkeletonProps) {
	return (
		<div className='bg-surface-card rounded-card shadow-card border border-surface-border overflow-hidden animate-pulse'>
			<div className='border-b border-surface-border px-4 py-3 flex gap-4'>
				{Array.from({ length: cols }).map((_, i) => (
					<div key={i} className='h-3 bg-surface-border rounded flex-1' />
				))}
			</div>
			<div className='divide-y divide-surface-border'>
				{Array.from({ length: rows }).map((_, i) => (
					<div key={i} className='px-4 py-3.5 flex gap-4 items-center'>
						{Array.from({ length: cols }).map((_, j) => (
							<div
								key={j}
								className={`h-3 bg-surface-border rounded flex-1 ${j === 0 ? 'max-w-[160px]' : ''}`}
							/>
						))}
					</div>
				))}
			</div>
		</div>
	);
}
